import * as _ from 'lodash';
import { CachedEventRegistry } from 'app/shared/events/cached_event_registry';
import {
    createTerrainProviderListFromJS,
    ImsTerrainProviderList,
} from 'app/config/ImsTerrainProviderList';
import { UserGeneratedConfig } from 'app/shared/collections/UserGeneratedConfig';
import { ConfigCollection } from 'app/shared/collections/config_collection.service';
import {
    createImageryProviderListFromJS,
    ImsImageryProviderList,
} from 'app/config/ImsImageryProviderList';
import {
    EmptyMapOptionsHelperFactory,
    MapOptionsHelperFactory,
} from 'app/overview/config/data/MapLayerOptionsHelper';
import { ImsTerrainProviderType } from 'app/config/ImsTerrainProvider';
import { ImsFeatureLayerProviderList } from 'app/config/ImsFeatureLayerProviderList';
import { Color } from 'app/shared/collections/StatDefinition';
import { ConfigService } from 'app/config/config.service';
import { ServiceNowEditableConfig } from 'app/overview/config/configuration_tab/ticket_management/service_now_config';

export type DynamicSchemaTypeDict = {
    asset: {
        [typeId: number]: string[];
    };
    ip_device: {
        [typeId: number]: string[];
    };
};

/**
 * All the config that can appear in the frontendconfig table
 */
export interface ImsUserConfig {
    baseGlobeColor: Color;
    connectionOutline: boolean;
    dynamicSchemaTypeDict: unknown;
    featureLayers: ImsFeatureLayerProviderList;
    heatMapChunkSize: number;
    heatMapDataLimit: number;
    imageryLayers: ImsImageryProviderList;
    liveLatency: boolean;
    removeBaseBingLayer: boolean;
    terrainProviders: ImsTerrainProviderList;
    throughputAsBits: boolean;
    serviceNow: ServiceNowEditableConfig;
}

// All the config that a user can create for themselves, as opposed to the functional config
export class UserGeneratedConfigService {
    frontEndConfigCollection: ConfigCollection;
    _configService: ConfigService;
    _frontEndConfig;
    _onChange;
    _onConfigLoad;
    /**
     * The subset of config.js that we are storing in the api
     */
    _fetchedApiConfigData: UserGeneratedConfig;

    // The full cached ims config. Only gets invalidated on
    // config updates from the API
    _cachedImsConfig: ImsUserConfig;

    private _initialLoad: boolean = false;

    private _imageryMapOptionsHelperFactory: MapOptionsHelperFactory;

    /**
     *
     * @param {ConfigCollection} configCollection
     * @param {MapOptionsHelperFactory} imageryMapOptionsHelperFactory
     * @param configService i.e. the old ConfigService
     */
    constructor(
        configCollection: ConfigCollection,
        imageryMapOptionsHelperFactory: MapOptionsHelperFactory,
        configService: ConfigService,
    ) {
        this.frontEndConfigCollection = configCollection;
        this._onChange = new CachedEventRegistry();
        this._onConfigLoad = new CachedEventRegistry();
        this._configService = configService;
        // Default the base config
        this._frontEndConfig = {
            terrainProviders: this._configService.terrainProviders,
            imageryLayers: [],
            featureLayers: [],
            heatMapDataLimit: this._configService.heatMapDataLimit,
            heatMapChunkSize: this._configService.heatMapChunkSize,
            connectionOutline: this._configService.connectionOutline,
            throughputAsBits: this._configService.throughputAsBits,
            baseGlobeColor: this._configService.baseGlobeColor,
            dynamicSchemaTypeDict: this._configService.dynamicSchemaTypeDict,
            serviceNow: { enabled: false },
        };
        this._fetchedApiConfigData = null;
        this._imageryMapOptionsHelperFactory = imageryMapOptionsHelperFactory;
        this._cachedImsConfig = null;
    }

    async init(): Promise<void> {
        // Make sure we have something before any events fire in case
        // configData() is called. In this case, the caller will get the
        // default config overlaid with the static config.js.
        this._updateCachedImsConfig();

        this.frontEndConfigCollection.addOnChangedListener((configs) => {
            const configIds = Object.keys(configs);
            const configLength = configIds.length;
            if (configLength > 1) {
                throw new Error(`Expect exactly one config to be returned but got ${configLength}`);
            }

            // TODO: hard-coded use of /api/frontend_config/1
            if (configLength === 1) {
                const configData = configs[configIds[0]];
                const oldFrontEndConfig = this._frontEndConfig;
                this._fetchedApiConfigData = configData;
                this._frontEndConfig = this._overLayConfigFromApiWithStaticConfig(
                    _.cloneDeep(configData),
                ); //consolidate the two configs

                if (!this._initialLoad) {
                    this._initialLoad = true;
                    // get the config out of the config by id dict
                    // noinspection JSIgnoredPromiseFromCall
                    this._onConfigLoad.notifyListeners(this._updateCachedImsConfig());
                } else if (!_.isEqual(oldFrontEndConfig, this._frontEndConfig)) {
                    return this._onChange.notifyListeners(this._updateCachedImsConfig());
                }
            }

            // if 0, or somehow less than 0, don't do anything
            return () => {
                // noop
            };
        });
    }

    onConfigLoad(callback: (unknown) => void): () => void {
        return this._onConfigLoad.addEventListener(callback);
    }

    onConfigChange(callback: (unknown) => void): () => void {
        return this._onChange.addEventListener(callback);
    }

    refreshData() {
        this.frontEndConfigCollection.refreshData();
    }

    /**
     * Returns an immutable cached ImsConfig config. Note this is only
     * what is contained in the API
     *
     * @returns {ImsUserConfig}
     */
    get configData(): ImsUserConfig {
        return this._cachedImsConfig;
    }

    async updateConfig(frontEndConfigValue: unknown, frontEndConfigKey: string): Promise<unknown> {
        // create the necessary key in the json or update it
        const frontEndConfig = this._createOrUpdateConfigOption(
            // use a clone
            this._fetchedApiConfigData.clone(),
            frontEndConfigValue,
            frontEndConfigKey,
        );
        return this._updateConfigOption(frontEndConfig);
    }

    /**
     * Creates the special ImsConfig object, transforming objects where necessary.
     *
     * The created cached ImsConfig is not mutable
     *
     * @returns {ImsUserConfig}
     * @private
     */
    private _updateCachedImsConfig(): ImsUserConfig {
        const config = this._overLayConfigFromApiWithStaticConfig(this._fetchedApiConfigData);
        // TODO - remove this when ArcGIS terrain providers are all working properly
        //   https://github.com/ftpsolutions/ims-mono/pull/43
        if (!this._configService.enableArcGisTerrain) {
            config.terrainProviders = config.terrainProviders?.filter(
                (t) => t.type !== ImsTerrainProviderType.ARCGIS,
            );
        }
        this._cachedImsConfig = deepFreeze({
            removeBaseBingLayer: config.removeBaseBingLayer,
            liveLatency: config.liveLatency,
            heatMapDataLimit: config.heatMapDataLimit,
            heatMapChunkSize: config.heatMapChunkSize,
            imageryLayers:
                this._imageryMapOptionsHelperFactory instanceof EmptyMapOptionsHelperFactory
                    ? []
                    : createImageryProviderListFromJS(
                          config.imageryLayers,
                          this._imageryMapOptionsHelperFactory.createHelper(),
                      ),
            terrainProviders: createTerrainProviderListFromJS(config.terrainProviders),
            dynamicSchemaTypeDict: config.dynamicSchemaTypeDict,
            connectionOutline: config.connectionOutline,
            throughputAsBits: config.throughputAsBits,
            baseGlobeColor: config.baseGlobeColor,
            serviceNow: Object.assign({}, config.serviceNow),
        });
        return this._cachedImsConfig;
    }

    /**
     * Overlays configs.
     * Config from API > Contents of config.js > contents of default.config.value
     * @param {UserGeneratedConfig} configFromApi
     * @returns {*}
     * @private
     */
    _overLayConfigFromApiWithStaticConfig(configFromApi: UserGeneratedConfig) {
        const newConfig = Object.assign({}, this._frontEndConfig);
        if (this._configService.enableApiStoredFrontEndConfig && !_.isEmpty(configFromApi)) {
            Object.entries(configFromApi.jsonConfig).map((configValue) => {
                newConfig[configValue[0]] = configValue[1];
            });
        }
        return newConfig;
    }

    _updateConfigOption(updatedFrontEndConfigOption: any): Promise<unknown> {
        if (!this._configService.enableApiStoredFrontEndConfig) {
            return Promise.reject(
                `Can't save a config option as api stored front end config is disabled`,
            );
        }

        return this.frontEndConfigCollection
            .updateObject(updatedFrontEndConfigOption)
            .then((newConfig: UserGeneratedConfig) => {
                this._fetchedApiConfigData = newConfig;
                return this._updateCachedImsConfig();
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    }

    _createOrUpdateConfigOption(
        fetchedApiConfig: UserGeneratedConfig,
        frontEndConfigValue: any,
        frontEndConfigKey: string,
    ): any {
        if (_.isEmpty(fetchedApiConfig)) {
            throw new Error(
                `Developer error, this shouldn't happen because you cant post to the endpoint`,
            );
        } else {
            // Overwrite the specific json field that we want to write.
            if (_.isFunction(frontEndConfigValue.toJS)) {
                // Imagery layers and terrain config layers use a complicated object.
                // Turn it back to a plain old javascript object just before saving.
                // Consider moving all of this back into the CustomIMSConfig class at some point...
                fetchedApiConfig.jsonConfig[frontEndConfigKey] = frontEndConfigValue.toJS();
            } else {
                fetchedApiConfig.jsonConfig[frontEndConfigKey] = frontEndConfigValue;
            }
            return fetchedApiConfig;
        }
    }
}

function deepFreeze(object) {
    // Borrowed straight from the Mozilla freeze example.
    // Don't call with cycles in the reference graph or you'll have a bad time!
    // Retrieve the property names defined on object
    const propNames = Object.getOwnPropertyNames(object);

    // Freeze properties before freezing self
    for (const name of propNames) {
        const value = object[name];

        if (value && typeof value === 'object') {
            deepFreeze(value);
        }
    }

    return Object.freeze(object);
}
